/**
 * Account-level location consent (one-time, before the first clock-in).
 *
 * Stored on the user's own profile row (`location_consent_given` +
 * `location_consent_at`) so the answer follows the guard across devices. This is
 * separate from — and does not bypass — the browser/OS location permission
 * prompt, and separate from the per-device live-sharing preference in
 * `live-location-consent.ts`.
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getLiveLocationConsent } from "@/lib/live-location-consent";

export type ProfileConsentStatus = "loading" | "unset" | "given";

/** Fired when any surface wants the consent screen re-opened. */
export const OPEN_LOCATION_CONSENT_EVENT = "mr:open-location-consent";

export function openLocationConsentScreen() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_LOCATION_CONSENT_EVENT));
}

const CHANGED_EVENT = "mr:location-consent-profile-changed";

export function useProfileLocationConsent(userId: string | null | undefined) {
  const [status, setStatus] = useState<ProfileConsentStatus>("loading");
  const [consentAt, setConsentAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) { setStatus("loading"); return; }
    const { data, error } = await supabase
      .from("profiles")
      .select("location_consent_given, location_consent_at")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      // Never block the screen on a read failure; treat as not yet answered.
      setStatus("unset");
      return;
    }
    const row = data as { location_consent_given?: boolean | null; location_consent_at?: string | null } | null;
    if (row?.location_consent_given) {
      setConsentAt(row.location_consent_at ?? null);
      setStatus("given");
      return;
    }
    // Existing users who already accepted on this device are not re-interrupted;
    // their answer is carried onto the profile once.
    if (getLiveLocationConsent(userId) === "granted") {
      const now = new Date().toISOString();
      await supabase
        .from("profiles")
        .update({ location_consent_given: true, location_consent_at: now } as never)
        .eq("id", userId);
      setConsentAt(now);
      setStatus("given");
      return;
    }
    setStatus("unset");
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const sync = () => { void load(); };
    window.addEventListener(CHANGED_EVENT, sync);
    return () => window.removeEventListener(CHANGED_EVENT, sync);
  }, [load]);

  const agree = useCallback(async () => {
    if (!userId) return false;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("profiles")
      .update({ location_consent_given: true, location_consent_at: now } as never)
      .eq("id", userId);
    if (error) return false;
    setConsentAt(now);
    setStatus("given");
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(CHANGED_EVENT));
    return true;
  }, [userId]);

  return { status, consentAt, agree, reload: load } as const;
}
